import type { useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type {
  AppointmentDocumentationSnapshotResponse,
  AppointmentEnhanceRequest,
  AppointmentEnhanceResponse,
  AppointmentManualGenerationErrorCode,
  AppointmentManualGenerationRequest,
  AppointmentManualGenerationResponse,
  AppointmentReEnhanceRequest,
  AppointmentReEnhanceResponse,
} from "../../../../shared/api-types";
import {
  ApiRequestError,
  NetworkRequestError,
  authenticatedApi,
  fetchWithAuthRetry,
} from "@/lib/api";
import {
  AppointmentDocumentationState,
  APPOINTMENT_DOCUMENTATION_POLL_INTERVAL_MS,
  appointmentDraftGenerationFromSnapshot,
  type AppointmentDocumentationOperation,
  type AppointmentDocumentationUiSnapshot,
  type AppointmentEnhanceRequestOutcome,
  type AppointmentEnhancementProgressSignal,
  type AppointmentManualGenerationInput,
  type AppointmentManualGenerationRequestOutcome,
  type AppointmentReEnhanceInput,
  type AppointmentReEnhanceRequestOutcome,
} from "@/lib/appointment-documentation-state";
import {
  enhancementProgressTarget,
  followsDraftText,
  openEnhancementProgressStream,
} from "@/lib/appointment-documentation-stream";
import { soapDraftDeliveryFromSnapshot } from "@/lib/soap-draft-delivery";
import { useFeatureEnabled } from "@/lib/use-features";
import { SOAP_PROGRESS_STREAM_FLAG } from "../../../../shared/features";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

async function readDocumentationSnapshot(
  getToken: ClerkGetToken,
  appointmentId: string,
  rdId?: string
): Promise<AppointmentDocumentationSnapshotResponse> {
  const scope = rdId ? `?rdId=${encodeURIComponent(rdId)}` : "";
  const response = await authenticatedApi<{
    data: AppointmentDocumentationSnapshotResponse;
  }>(
    getToken,
    `/api/appointments/${encodeURIComponent(appointmentId)}/documentation${scope}`
  );

  return response.data;
}

export function useReadOnlyAppointmentDocumentation({
  appointmentId,
  rdId,
  getToken,
}: {
  appointmentId: string;
  rdId: string;
  getToken: ClerkGetToken;
}) {
  const failureCaptured = useRef(false);
  const query = useQuery({
    queryKey: ["appointment-documentation", appointmentId, rdId],
    queryFn: () => readDocumentationSnapshot(getToken, appointmentId, rdId),
    refetchInterval: APPOINTMENT_DOCUMENTATION_POLL_INTERVAL_MS,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (query.data) failureCaptured.current = false;

    if (!query.error || failureCaptured.current) return;

    failureCaptured.current = true;
    captureDocumentationFailure(query.error, "poll");
  }, [query.data, query.error]);

  return {
    generation: appointmentDraftGenerationFromSnapshot(query.data ?? null),
    writeback: soapDraftDeliveryFromSnapshot(query.data ?? null),
    unavailable: query.isError,
    newerHealthieTranscript: query.data?.newerHealthieTranscript === true,
  };
}

async function requestEnhancement(
  getToken: ClerkGetToken,
  appointmentId: string,
  claimKey: string
): Promise<AppointmentEnhanceRequestOutcome> {
  try {
    const response = await authenticatedApi<{ data: AppointmentEnhanceResponse }>(
      getToken,
      `/api/appointments/${encodeURIComponent(appointmentId)}/enhance`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ claimKey } satisfies AppointmentEnhanceRequest),
      }
    );

    return response.data;
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 501) {
      return { status: "unavailable" };
    }

    throw error;
  }
}

/**
 * NO-775: posts the pasted text. A refusal carries the server's closed code
 * for the dialog; the text itself never reaches Sentry or a log, because only
 * the classified failure is captured.
 */
async function requestManualGeneration(
  getToken: ClerkGetToken,
  appointmentId: string,
  input: AppointmentManualGenerationInput
): Promise<AppointmentManualGenerationRequestOutcome> {
  try {
    const response = await authenticatedApi<{
      data: AppointmentManualGenerationResponse;
    }>(
      getToken,
      `/api/appointments/${encodeURIComponent(appointmentId)}/manual-generation`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input satisfies AppointmentManualGenerationRequest),
      }
    );

    return response.data;
  } catch (error) {
    const code = manualGenerationRefusalCode(error);
    if (code) return { status: "refused", code };

    throw error;
  }
}

const MANUAL_GENERATION_CODES = new Set<string>([
  "invalid_request",
  "field_too_large",
  "manual_generation_unavailable",
  "transcript_exists",
  "appointment_identity_incomplete",
  "appointment_identity_ambiguous",
  "appointment_identity_unresolved",
  "session_conflict",
  "generation_not_started",
] satisfies AppointmentManualGenerationErrorCode[]);

function manualGenerationRefusalCode(
  error: unknown
): AppointmentManualGenerationErrorCode | null {
  if (!(error instanceof ApiRequestError)) return null;

  const code = (error.body as { code?: unknown } | undefined)?.code;

  if (typeof code === "string" && MANUAL_GENERATION_CODES.has(code)) {
    return code as AppointmentManualGenerationErrorCode;
  }

  return error.status === 501 ? "manual_generation_unavailable" : null;
}

/**
 * NO-785: posts a Re-enhance. The three 409 refusals and a 501 come back as
 * closed outcomes; anything else throws for the classified Sentry capture.
 */
async function requestReEnhancement(
  getToken: ClerkGetToken,
  appointmentId: string,
  input: AppointmentReEnhanceInput
): Promise<AppointmentReEnhanceRequestOutcome> {
  try {
    const response = await authenticatedApi<{ data: AppointmentReEnhanceResponse }>(
      getToken,
      `/api/appointments/${encodeURIComponent(appointmentId)}/re-enhance`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input satisfies AppointmentReEnhanceRequest),
      }
    );

    return response.data;
  } catch (error) {
    const refusal = reEnhanceRefusal(error);
    if (refusal) return refusal;

    throw error;
  }
}

function reEnhanceRefusal(error: unknown): AppointmentReEnhanceRequestOutcome | null {
  if (!(error instanceof ApiRequestError)) return null;

  if (error.status === 501) return { status: "unavailable" };

  const body = (error.body ?? {}) as {
    code?: unknown;
    currentDraftRevisionId?: unknown;
  };

  if (
    body.code === "draft_changed" &&
    typeof body.currentDraftRevisionId === "string"
  ) {
    return {
      status: "draft_changed",
      currentDraftRevisionId: body.currentDraftRevisionId,
    };
  }

  if (body.code === "draft_approved") return { status: "draft_approved" };

  return body.code === "no_draft" ? { status: "no_draft" } : null;
}

const DOCUMENTATION_FAILURE_SENTINELS: Record<
  AppointmentDocumentationOperation,
  { message: string; operation: string }
> = {
  poll: { message: "appointment_documentation_poll_failed", operation: "snapshot" },
  enhance: {
    message: "appointment_documentation_enhance_failed",
    operation: "enhance",
  },
  manual_generation: {
    message: "appointment_documentation_manual_generation_failed",
    operation: "manual_generation",
  },
  re_enhance: {
    message: "appointment_documentation_re_enhance_failed",
    operation: "re_enhance",
  },
};

function captureDocumentationFailure(
  error: unknown,
  operation: AppointmentDocumentationOperation
): void {
  const failure = classifyDocumentationFailure(error);
  const sentinel = DOCUMENTATION_FAILURE_SENTINELS[operation];
  Sentry.captureException(new Error(sentinel.message), {
    tags: {
      ...failure,
      operation: sentinel.operation,
      source: "client",
      surface: "appointment_documentation",
    },
  });
}

function classifyDocumentationFailure(error: unknown): {
  code: "network_error" | "http_error" | "unexpected_response";
  status?: number;
} {
  if (error instanceof NetworkRequestError) return { code: "network_error" };

  if (error instanceof ApiRequestError) {
    return { code: "http_error", status: error.status };
  }

  return { code: "unexpected_response" };
}

export function useAppointmentDocumentation({
  appointmentId,
  getToken,
}: {
  appointmentId: string;
  getToken: ClerkGetToken;
}) {
  const [ui, setUi] = useState<AppointmentDocumentationUiSnapshot>(() => ({
    connection: "connecting",
    snapshot: null,
    enhanceAction: "hidden",
    generation: { phase: "idle" },
  }));
  const controllerRef = useRef<AppointmentDocumentationState | null>(null);
  const pollFailureCapturedRef = useRef(false);

  if (!controllerRef.current) {
    controllerRef.current = new AppointmentDocumentationState(
      () => readDocumentationSnapshot(getToken, appointmentId),
      (claimKey) => requestEnhancement(getToken, appointmentId, claimKey),
      (next) => {
        if (next.connection === "live") pollFailureCapturedRef.current = false;

        setUi(next);
      },
      {
        // Polling repeats every few seconds, so only the first failure of a
        // disconnection window reaches Sentry; recovery re-arms the capture.
        onError: (error, operation) => {
          if (operation === "poll") {
            if (pollFailureCapturedRef.current) return;

            pollFailureCapturedRef.current = true;
          }

          captureDocumentationFailure(error, operation);
        },
        requestManualGeneration: (input) =>
          requestManualGeneration(getToken, appointmentId, input),
        requestReEnhancement: (input) =>
          requestReEnhancement(getToken, appointmentId, input),
      }
    );
  }

  const controller = controllerRef.current;

  useEffect(() => {
    controller.start();

    return () => controller.dispose();
  }, [controller]);

  useEnhancementProgressStream(controller, appointmentId, getToken, ui);

  return {
    ui,
    enhance: () => controller.enhance(),
    refresh: () => controller.refresh(),
    generateFromPastedTranscript: (input: AppointmentManualGenerationInput) =>
      controller.generateFromPastedTranscript(input),
    reEnhance: (input: AppointmentReEnhanceInput) => controller.reEnhance(input),
  };
}

/**
 * NO-772: follows the running attempt's content-free progress while
 * `soap-progress-stream` is on. Keyed on the generation, so a retry opens a
 * fresh stream and a settled snapshot closes it; polling never stops.
 */
function useEnhancementProgressStream(
  controller: AppointmentDocumentationState,
  appointmentId: string,
  getToken: ClerkGetToken,
  ui: AppointmentDocumentationUiSnapshot
): void {
  const enabled = useFeatureEnabled(SOAP_PROGRESS_STREAM_FLAG);
  const generationId = enhancementProgressTarget(enabled, ui.snapshot);
  const followText = followsDraftText(ui.snapshot);
  // A fresh token getter must not reopen a healthy stream.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    if (!generationId) return;

    const stream = {
      appointmentId,
      request: (path: string, init: RequestInit) =>
        fetchWithAuthRetry(getTokenRef.current, path, init),
      onSignal: (signal: AppointmentEnhancementProgressSignal) =>
        controller.applyEnhancementProgress(signal),
    };
    const closeProgress = openEnhancementProgressStream(stream);

    // NO-772 scope change: the draft text, a clinical read, into the locked
    // preview. NO-785: a Re-enhance never shows its text, so it reads none.
    const closeText = followText
      ? openEnhancementProgressStream({
          ...stream,
          onText: (line) => controller.applyGenerationText(line),
        })
      : () => {};

    // Turning the flag off, or the attempt leaving the snapshot, drops "Writing".
    return () => {
      closeProgress();
      closeText();
      controller.applyEnhancementProgress({ type: "interrupted" });
    };
  }, [controller, appointmentId, generationId, followText]);
}
